'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { TopNav } from '@/components/top-nav';

/**
 * UserAreaShell — 用户中心页面的统一 wrapper
 *
 * 2026-07-07 v19 改造:
 * - 之前 (v18): useEffect 检测 iframe 后才决定是否渲染 TopNav
 *   问题: SSR 阶段 mounted=false → 默认渲染 TopNav → 浏览器先显示 TopNav
 *   → client mount 后才移除 → 视觉上闪一下 (老板反馈)
 * - 现在 (v19): 接受 ssrInIframe prop (从 page.tsx server component 传过来,
 *   在 SSR 阶段就知道是否在 iframe 里)
 *   page.tsx 读 searchParams.inframe=1 (外层 ThinkPHP iframe URL 加这个 query),
 *   SSR 阶段就知道要跳过 TopNav
 *
 * 用法:
 *   // page.tsx (server)
 *   export default function Page({ searchParams }) {
 *     const inIframe = searchParams?.inframe === '1';
 *     return <FeedbackListClient inIframe={inIframe} />;
 *   }
 *
 *   // client component
 *   <UserAreaShell inIframe={inIframe}>
 *     ...page content...
 *   </UserAreaShell>
 *
 * 双保险:
 * - SSR 阶段用 ssrInIframe prop 决定 (0ms 渲染决定, 不闪烁)
 * - client mount 后也用 window.self !== window.top 检测 (防止 query 没传)
 *   - 如果 SSR 和 client 检测结果不一致, 用 client 的 (但不会闪烁, 因为 layout 高度没变)
 */
export function UserAreaShell({
  children,
  inIframe: ssrInIframe = false,
}: {
  children: ReactNode;
  inIframe?: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  const [isInIframe, setIsInIframe] = useState(ssrInIframe);

  useEffect(() => {
    setMounted(true);
    try {
      setIsInIframe(window.self !== window.top);
    } catch (e) {
      // 跨域 iframe 访问 window.top 会抛 SecurityError
      setIsInIframe(true);
    }
  }, []);

  if (isInIframe) {
    // iframe 嵌入 — 只渲染 children, 不挂载 TopNav
    return <>{children}</>;
  }

  // 直接访问 — 渲染 TopNav + children
  return (
    <>
      <TopNav />
      {children}
    </>
  );
}